<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useCacheStore } from '@/modules/cache/stores/cache.store'
import { useClientStore } from '@/modules/clients/stores/client.store'
import { useSideMenuStore } from '@/modules/side-menu/stores/side-menu'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useViewport } from '@/modules/app/composables/use-viewport'
import { REFRESH_HOME, REFRESH_MOTION } from '@/constants'

import IndeterminateLoader from '@/shared/components/loader/IndeterminateLoader.vue'
import VersionUpdateMenu from '@/modules/version/components/VersionUpdateMenu.vue'
import VersionUpdateDialog from '@/modules/version/components/VersionUpdateDialog.vue'
import ClientInputSelect from '@/modules/clients/components/ClientInputSelect.vue'
import UserNotificationMenu from '@/modules/users/components/UserNotificationMenu.vue'
import UserMenu from '@/modules/users/components/UserMenu.vue'
import ViewMenu from '@/modules/app/components/ViewMenu.vue'
import AlertMenu from '@/modules/app/components/AlertMenu.vue'
import eventBus from '@/event-bus'

const route = useRoute()

const appStore = useAppStore()
const userStore = useUserStore()
const cacheStore = useCacheStore()
const clientStore = useClientStore()
const filterStore = useFilterStore()
const sideMenu = useSideMenuStore()

const { isDesktop } = useViewport()

const emits = (): void => {
  eventBus.emit(REFRESH_HOME)
  eventBus.emit(REFRESH_MOTION)
}

const onClientChange = async (): Promise<void> => {
  if (!userStore.user) return

  filterStore.clearTag()

  appStore.isIndeterminate = true

  await appStore.isClientReady(route.name)

  appStore.isIndeterminate = false

  emits()
  sideMenu.close()
}

const onViewChange = async (): Promise<void> => {
  appStore.isIndeterminate = true

  await appStore.isViewReady(route.name)

  appStore.isIndeterminate = false

  emits()
}

onMounted(() => cacheStore.startCacheUpdate())

onUnmounted(() => cacheStore.clearCacheTimer())

watch(
  () => cacheStore.hasApplicationSettingUpdate,
  (newVal) => {
    if (!newVal) return

    appStore.initApp()
  },
)

watch(
  () => cacheStore.hasClientSettingUpdate,
  (newVal) => {
    if (!newVal) return
    clientStore.loadClientSettings(route.name)
  },
)

watch(
  () => cacheStore.hasUserUpdate,
  (newVal) => {
    if (!newVal) return

    appStore.loadClientFilter()
  },
)

watch(
  () => route.name,
  (newVal, oldValue) => {
    if (!oldValue) return
    clientStore.loadClientSettings(newVal)
  },
)
</script>

<template>
  <IndeterminateLoader class="fixed z-50 top-0 left-0" :loading="appStore.isIndeterminate" />
  <header
    class="z-40 fixed left-0 top-0 right-0 bg-black transition-left linear duration-200 shadow-md py-1"
    :class="userStore.user && 'lg:left-14'"
  >
    <div class="flex items-center justify-start">
      <div class="flex shrink-0 items-center mx-4">
        <img :alt="$t('glossary.logo')" src="@/assets/images/logo.png" class="w-6 h-6" />
      </div>
      <div class="flex items-center text-2xl whitespace-nowrap tracking-wider">
        <span class="text-white">{{ $t('glossary.our') }}</span>
        <span class="text-primary">{{ $t('glossary.box', 2) }}</span>
      </div>
      <template v-if="userStore.user">
        <div class="flex flex-1 items-center justify-between ms-10">
          <div v-if="isDesktop" class="flex items-center">
            <ClientInputSelect
              v-if="userStore.user.activeClient && userStore.user.clients.length"
              v-model="userStore.user.activeClient"
              :title="$t('glossary.account')"
              :clients="userStore.user.clients"
              class="dark min-w-84"
              @change="onClientChange()"
            />
            <ViewMenu
              v-model="clientStore.activeViewId"
              :title="$t('glossary.view')"
              :views="clientStore.views"
              class="dark w-max"
              @change="onViewChange()"
            />
          </div>
          <div v-if="isDesktop" class="flex items-center gap-2 me-3">
            <slot name="search"></slot>
            <AlertMenu />
            <UserNotificationMenu />
            <VersionUpdateMenu />
            <UserMenu />
          </div>
        </div>
      </template>
    </div>
  </header>
  <VersionUpdateDialog />
</template>
